import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import AboutSection from '../components/AboutSection';
import SkillsDisplay from '../components/SkillsDisplay';
import ProjectsDisplay from '../components/ProjectsDisplay';
import ContactSection from '../components/CopyableContacts';
import FloatingElements from '../components/FloatingElements';
import LoadingScreen from '../components/LoadingScreen';
import duckImage from '../images/duck.gif';

const SITE_DATA = {
    skills: {
        languages: [
            { name: "JavaScript", level: "Advanced" },
            { name: "Python", level: "Advanced" },
            { name: "C/C++", level: "Advanced" },
            { name: "Java", level: "Intermediate" },
            { name: "Rust", level: "Intermediate" },
        ],
        frontend: [
            { name: "React", level: "Advanced" },
            { name: "Next.js", level: "Advanced" },
            { name: "TailwindCSS", level: "Advanced" },
            { name: "HTML/CSS", level: "Advanced" },
            { name: "Styled Components", level: "Intermediate" }
        ],
        backend: [
            { name: "Node.js", level: "Advanced" },
            { name: "Express.js", level: "Intermediate" },
            { name: "Docker", level: "Intermediate" },
            { name: "PostgreSQL", level: "Intermediate" }
        ],
        tools: [
            { name: "Git", level: "Advanced" },
            { name: "Figma", level: "Advanced" },
            { name: "Linux", level: "Intermediate" },
            { name: "AWS", level: "Intermediate" }
        ]
    },
    projects: [
        {
            name: "Squeak & Simmer",
            description: "A cozy cooking game where you help Squeak create dishes and perfect grandma's secret soup recipe - made in 3 days for Cozy Winter Jam 2026! Features 3 endings and 25+ unique dishes.",
            tech: ["Godot", "GDScript", "2D Art"],
            github: "buffbakery.itch.io/squeaknsimmer",
            images: [
                "/images/squeak/img1.png",
                "/images/squeak/img2.png",
                "/images/squeak/img3.png"
            ]
        },
        {
            name: "AlgoPath",
            description: "Interactive algorithm decision tree for competitive programming. Answer yes/no questions to find the right technique - with code snippets, complexity analysis, and common pitfalls. Built on the KACTL contest reference.",
            tech: ["React", "TypeScript", "Tailwind"],
            github: "ashtherz.github.io/kactl",
            images: [
                "/images/algopath/img1.png",
                "/images/algopath/img2.png",
                "/images/algopath/img3.png"
            ]
        },
        {
            name: "CollabAI",
            description: "Award-winning RAG learning assistant that helps students access information from educational materials - Best Use of DataStax API at UNIHACK 2025!",
            tech: ["React", "Next.js", "Tailwind", "PostgreSQL", "Prisma", "DataStax", "Langflow"],
            github: "devpost.com/software/collabai-vhqk0n",
            images: [
                "/images/collabai/img1.png",
                "/images/collabai/img2.png",
                "/images/collabai/img3.png"
            ]
        },
        {
            name: "APIOverflow",
            description: "A super cool API hub library that helps developers discover, share, and manage APIs efficiently.",
            tech: ["TypeScript", "React", "Tailwind", "Python", "Prisma", "MongoDB", "Docker"],
            github: "github.com/unsw-cse-comp99-3900/APIOverflow",
            images: [
                "/images/apioverflow/img1.png",
                "/images/apioverflow/img2.png",
                "/images/apioverflow/img3.png",
                "/images/apioverflow/img4.png",
                "/images/apioverflow/img5.png"
            ]
        },
        {
            name: "Nerd Station",
            description: "Award-winning study platform combining study tools with gamification elements - 3rd place at Google DSC Hackathon!",
            tech: ["TypeScript", "Figma", "Tailwind"],
            github: "gdsc-hackathon-2023.web.app",
            images: [
                "/images/nerd-station/img1.png",
                "/images/nerd-station/img2.png",
                "/images/nerd-station/img3.png"
            ]
        },
        {
            name: "SPLIT",
            description: "A web application that helps users easily split bills within large groups and track expenses by scanning or manually adding bills.",
            tech: ["Docker", "Express.js", "Next.js", "React", "Shadcn", "Tailwind"],
            github: "devpost.com/software/split-utm8ph",
            images: [
                "/images/split/img1.png",
                "/images/split/img2.png",
                "/images/split/img3.png",
                "/images/split/img4.png",
                "/images/split/img5.png"
            ]
        },
        {
            name: "BeBetter",
            description: "Social fitness app that calculates calories and exercise requirements, built during CSESoc x DevSoc Hackathon.",
            tech: ["Express.js", "GSAP", "Next.js", "React", "Tailwind", "TypeScript"],
            github: "devpost.com/software/bebetter-vqs9fg",
            images: [
                "/images/bebetter/img1.png",
                "/images/bebetter/img2.png",
                "/images/bebetter/img3.png",
                "/images/bebetter/img4.png"
            ]
        },
        {
            name: "Pi Website",
            description: "Interactive website to help memorize and recite 314 digits of pi accurately. A fun challenge for number enthusiasts!",
            tech: ["HTML", "CSS", "JavaScript"],
            github: "ashtherz.github.io/pi-cat/",
            images: ["/images/pi/img1.png"]
        },
        {
            name: "Society Marketing Bot",
            description: "A Discord bot that streamlines society marketing requests and approvals through an interactive form system.",
            tech: ["Discord.py", "Python"],
            github: "github.com/ashtherz/cpmsoc-bot",
            images: ["/images/cpm-bot/img1.png", "/images/cpm-bot/img2.png"]
        }
    ],
    education: [
        {
            institution: "University of New South Wales",
            degree: "Bachelor of Computer Science (Honors)",
            years: "2022-2026"
        }
    ],
    hobbies: [
        "Sleeping",
        "Coding",
        "Anime",
        "Genshin",
        "Pokemon TCG",
    ],
    contacts: {
        email: "estherdjaidiguna@gmail.com",
        discord: "ashtherz",
        linkedin: "https://www.linkedin.com/in/esther-d-5629a91aa/",
        genshinUid: "822583810 (Asia)",
        projectSekaiId: "417921248480268288"
    }
};

const Home = () => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Simulate loading time
        const loadingTimer = setTimeout(() => {
            setIsLoading(false);
        }, 2500);

        return () => clearTimeout(loadingTimer);
    }, []);

    if (isLoading) {
        return <LoadingScreen duckImage={duckImage} />;
    }

    return (
        <div className="min-h-screen bg-cute-dark relative overflow-x-hidden">
            {/* Floating Background Elements */}
            <FloatingElements />

            {/* Navigation */}
            <Navbar />

            {/* Main Content */}
            <main className="relative z-10">
                {/* Hero Section */}
                <HeroSection />

                {/* About Section */}
                <AboutSection
                    education={SITE_DATA.education}
                    hobbies={SITE_DATA.hobbies}
                />

                {/* Skills Section */}
                <SkillsDisplay skills={SITE_DATA.skills} />

                {/* Projects Section */}
                <ProjectsDisplay projects={SITE_DATA.projects} />

                {/* Contact Section */}
                <ContactSection contacts={SITE_DATA.contacts} />
            </main>

            {/* Back to Top Button */}
            <BackToTopButton />
        </div>
    );
};

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const toggleVisibility = () => {
            if (window.pageYOffset > 500) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <button
            onClick={scrollToTop}
            className={`fixed bottom-6 right-6 p-3 rounded-full bg-white text-black shadow-lg transition-all duration-300 z-50 hover:scale-110 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
            }`}
            aria-label="Back to top"
        >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
        </button>
    );
};

export default Home;
